/**
 * Product page — variant picker, quantity, description clamp, sticky panel,
 * disclosure rows and share.
 *
 * The variant picker is the interesting part. The page ships a `<noscript>`
 * select posting `id` directly, so it stays buyable with this file blocked.
 * What the script adds is the live part:
 *   * resolving the chosen options to a variant and writing its id into the
 *     hidden `id` input;
 *   * marking combinations that do not exist, so a shopper can see a size is
 *     gone in this colour and still pick it to re-resolve the rest;
 *   * updating price, SKU, stock and the button label in place;
 *   * putting `?variant=` in the address bar so the page is shareable and the
 *     back button behaves.
 *
 * `--ProductMain_Height`. The sticky card's inset-block-start is
 *
 *     min(calc(--Site_Margin + --Announcement_Height),
 *         calc(100dvh - --Site_Margin - var(--ProductMain_Height, 0px)))
 *
 * so a card taller than the viewport parks its bottom edge instead of its top.
 * Only the measurement lives here; the min() in the class string does the rest.
 *
 * ES5 only — no build step.
 */
(function () {
  'use strict';

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  function money(cents) {
    var format = (window.Theme && window.Theme.moneyFormat) || '${{amount}}';
    var amount = (cents / 100).toFixed(2);
    var parts = amount.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var withCommas = parts.join('.');
    return format
      .replace(/\{\{\s*amount\s*\}\}/g, withCommas)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, parts[0])
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withCommas.replace(/,/g, '.').replace(/\.(\d\d)$/, ',$1'))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, parts[0].replace(/,/g, '.'));
  }

  /* ------------------------------------------------------------------ 1 */
  /* Description clamp. The control only appears when the copy overflows, so a
     one-line description never gets a dead "Read more" under it. */
  function initReadMore() {
    $$('[data-product-description]').forEach(function (wrap) {
      var text = $('[data-description-text]', wrap);
      var toggle = $('[data-description-toggle]', wrap);
      if (!text || !toggle) return;

      if (text.scrollHeight <= text.clientHeight + 1) {
        toggle.remove();
        wrap.setAttribute('data-expanded', 'true');
        return;
      }
      toggle.hidden = false;

      toggle.addEventListener('click', function () {
        var expanded = wrap.getAttribute('data-expanded') === 'true';
        wrap.setAttribute('data-expanded', expanded ? 'false' : 'true');
        toggle.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        toggle.textContent = expanded
          ? (toggle.getAttribute('data-label-more') || 'Read more')
          : (toggle.getAttribute('data-label-less') || 'Read less');
      });
    });
  }
  /* ------------------------------------------------------------------ 2 */
  /* The variant picker.
   *
   * Two control shapes — swatches and the dropdown — behind one contract, so
   * nothing below has to know which it is looking at:
   *
   *     [data-option-control]   the root, with data-option-index and data-value
   *
   * The option paired with the Add to bag button lives outside the picker, in
   * the buy row, so these are queried across the whole section rather than
   * inside one wrapper.
   */
  function initVariants() {
    var form = $('[data-product-form]');
    var dataEl = $('[data-variant-data]');
    if (!form || !dataEl) return;

    var controls = $$('[data-option-control]').sort(function (a, b) {
      return (+a.getAttribute('data-option-index')) - (+b.getAttribute('data-option-index'));
    });
    if (!controls.length) return;

    var variants;
    try { variants = JSON.parse(dataEl.textContent); } catch (e) { return; }
    if (!variants || !variants.length) return;

    var idInput = $('[data-variant-input]', form);
    var picker = $('[data-variant-picker]');

    function chosen() {
      return controls.map(function (control) {
        return control.getAttribute('data-value') || null;
      });
    }

    function findVariant(values) {
      for (var i = 0; i < variants.length; i++) {
        var variant = variants[i];
        var match = true;
        for (var j = 0; j < values.length; j++) {
          if (values[j] === null || variant.options[j] !== values[j]) { match = false; break; }
        }
        if (match) return variant;
      }
      return null;
    }

    /* Mark values that cannot be reached from the choices already made in the
     * *earlier* options. Looking only backwards is deliberate: judging an option
     * against every later one too would grey out almost everything the moment a
     * shopper picks a rare size.
     *
     * Marked, not disabled — the shopper can still pick one, which re-resolves
     * the options after it. Disabling dead-ends them instead. */
    function markAvailability(values) {
      controls.forEach(function (control, index) {
        var options = $$('[data-size-option]', control);
        var inputs = $$('[data-option-input]', control);

        function judge(value) {
          var candidate = values.slice();
          candidate[index] = value;
          for (var i = 0; i < variants.length; i++) {
            var variant = variants[i];
            if (!variant.available) continue;
            var ok = true;
            for (var j = 0; j <= index; j++) {
              if (candidate[j] === null) continue;
              if (variant.options[j] !== candidate[j]) { ok = false; break; }
            }
            if (ok) return true;
          }
          return false;
        }

        options.forEach(function (button) {
          var reachable = judge(button.getAttribute('data-size-value'));
          button.setAttribute('data-unavailable', reachable ? 'false' : 'true');
        });

        inputs.forEach(function (input) {
          var reachable = judge(input.value);
          var label = document.querySelector('label[for="' + input.id + '"]');
          if (label) label.setAttribute('data-unavailable', reachable ? 'false' : 'true');
        });
      });
    }

    function setValue(control, value) {
      control.setAttribute('data-value', value);

      var echo = $('[data-option-echo]', control);
      if (echo) echo.textContent = value;

      var label = $('[data-size-label]', control);
      if (label) label.textContent = value;

      $$('[data-size-option]', control).forEach(function (button) {
        var selected = button.getAttribute('data-size-value') === value;
        var item = button.parentNode;
        if (item && item.setAttribute) item.setAttribute('aria-selected', selected ? 'true' : 'false');
      });

      $$('[data-option-input]', control).forEach(function (input) {
        input.checked = input.value === value;
      });
    }

    function update(pushUrl) {
      var values = chosen();
      markAvailability(values);

      var variant = findVariant(values);
      var button = $('[data-add-to-cart]', document);
      var atcLabel = $('[data-atc-label]', document);

      if (!variant) {
        if (idInput) idInput.value = '';
        if (button) button.disabled = true;
        if (atcLabel && atcLabel.getAttribute('data-unavailable-text')) {
          atcLabel.textContent = atcLabel.getAttribute('data-unavailable-text');
        }
        return;
      }

      if (idInput) idInput.value = variant.id;
      if (button) button.disabled = !variant.available;
      if (atcLabel) {
        var key = variant.available ? 'data-add-text' : 'data-sold-out-text';
        var text = atcLabel.getAttribute(key);
        if (text) atcLabel.textContent = text;
      }

      var stock = $('[data-stock-status]', document);
      if (stock) {
        var stockText = stock.getAttribute(variant.available ? 'data-in-stock' : 'data-out-of-stock');
        if (stockText) stock.textContent = stockText;
      }

      var price = $('[data-price]', document);
      if (price) {
        price.textContent = money(variant.price);
        var onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
        price.classList.toggle('prd-Price-sale', !!onSale);

        var compare = $('[data-compare-price]', document);
        if (compare) {
          if (onSale) {
            compare.textContent = money(variant.compare_at_price);
            compare.hidden = false;
          } else {
            compare.hidden = true;
          }
        }
      }

      var sku = $('[data-sku]', document);
      if (sku) {
        sku.textContent = variant.sku || '';
        sku.hidden = !variant.sku;
      }

      if (pushUrl !== false && window.history && window.history.replaceState) {
        var url = (picker && picker.getAttribute('data-url')) || window.location.pathname;
        window.history.replaceState({}, '', url + '?variant=' + variant.id);
      }
    }

    /* The button's alternative labels are read off the DOM once, at the state
     * the server rendered, so no English is hard-coded here. */
    (function captureLabels() {
      var atcLabel = $('[data-atc-label]', document);
      if (atcLabel) {
        var current = atcLabel.textContent.trim();
        var variant = findVariant(chosen());
        atcLabel.setAttribute(
          variant && variant.available ? 'data-add-text' : 'data-sold-out-text', current);
      }
    })();

    /* ---- the dropdown -------------------------------------------------- */
    function closeAll(except) {
      controls.forEach(function (control) {
        if (control === except) return;
        control.setAttribute('data-size-open', 'false');
        var trigger = $('[data-size-trigger]', control);
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
      });
    }

    controls.forEach(function (control) {
      var trigger = $('[data-size-trigger]', control);
      if (trigger) {
        trigger.addEventListener('click', function (event) {
          event.stopPropagation();
          var open = control.getAttribute('data-size-open') === 'true';
          closeAll(control);
          control.setAttribute('data-size-open', open ? 'false' : 'true');
          trigger.setAttribute('aria-expanded', open ? 'false' : 'true');
        });
      }

      $$('[data-size-option]', control).forEach(function (button) {
        button.addEventListener('click', function () {
          setValue(control, button.getAttribute('data-size-value'));
          control.setAttribute('data-size-open', 'false');
          if (trigger) {
            trigger.setAttribute('aria-expanded', 'false');
            trigger.focus();
          }
          update();
        });
      });

      control.addEventListener('change', function (event) {
        var input = event.target;
        if (!input || !input.hasAttribute('data-option-input')) return;
        setValue(control, input.value);
        update();
      });

      control.addEventListener('keydown', function (event) {
        if (event.key !== 'Escape') return;
        if (control.getAttribute('data-size-open') !== 'true') return;
        event.stopPropagation();
        control.setAttribute('data-size-open', 'false');
        if (trigger) {
          trigger.setAttribute('aria-expanded', 'false');
          trigger.focus();
        }
      });
    });

    document.addEventListener('click', function (event) {
      if (event.target.closest && event.target.closest('[data-option-control]')) return;
      closeAll(null);
    });

    update(false);
  }

  /* ------------------------------------------------------------------ 3 */
  function initQuantity() {
    $$('[data-quantity-input]').forEach(function (input) {
      var wrap = input.parentNode;
      $$('[data-quantity-step]', wrap).forEach(function (button) {
        button.addEventListener('click', function () {
          var step = parseInt(button.getAttribute('data-quantity-step'), 10) || 1;
          var next = (parseInt(input.value, 10) || 1) + step;
          var min = parseInt(input.getAttribute('min'), 10) || 1;
          input.value = next < min ? min : next;
          /* Dispatch so anything listening (a cart-drawer line, an app) sees it. */
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    });
  }

  /* ------------------------------------------------------------------ 4 */
  /* The sticky purchase card measures itself into --ProductMain_Height. Only
     the value matters — the min() in the class string does the rest. */
  function initStickyPanel() {
    var panels = $$('[data-product-panel]');
    if (!panels.length) return;

    var frame = null;

    function measure() {
      frame = null;
      panels.forEach(function (panel) {
        /* Read the height with the property cleared, or the previous value
           feeds back through the card's own inset and drifts. */
        panel.style.removeProperty('--ProductMain_Height');
        var height = panel.offsetHeight;
        if (height) panel.style.setProperty('--ProductMain_Height', height + 'px');
      });
    }

    function schedule() {
      if (frame) return;
      frame = requestAnimationFrame(measure);
    }

    measure();
    window.addEventListener('resize', schedule);
    window.addEventListener('load', schedule);
    if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
      document.fonts.ready.then(schedule);
    }
    if (window.ResizeObserver) {
      var observer = new ResizeObserver(schedule);
      panels.forEach(function (panel) { observer.observe(panel); });
    }
  }

  /* ------------------------------------------------------------------ 5 */
  /* Share: the native sheet where the browser has one, the clipboard where it
     does not, and the URL selected for copying where neither is available. */
  function initShare() {
    $$('[data-share]').forEach(function (button) {
      button.addEventListener('click', function () {
        var url = button.getAttribute('data-url');
        var title = button.getAttribute('data-title') || document.title;
        var done = button.getAttribute('data-copied') || 'Copied';
        var original = button.textContent;

        function confirmCopy() {
          button.textContent = done;
          window.setTimeout(function () { button.textContent = original; }, 2000);
        }

        if (navigator.share) {
          navigator.share({ title: title, url: url }).catch(function () { /* dismissed */ });
          return;
        }
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(url).then(confirmCopy).catch(function () {
            window.prompt(title, url);
          });
          return;
        }
        window.prompt(title, url);
      });
    });
  }

  function init() {
    initReadMore();
    initVariants();
    initQuantity();
    initStickyPanel();
    initShare();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
